// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import 'd3-transition'
import {axisBottom, axisLeft} from 'd3-axis'
import {controller, target} from '@github/catalyst'
import {max as d3max, range} from 'd3-array'
import {easeBackOut, easeElasticOut} from 'd3-ease'
import {months, weekdays} from '../locale'
import {scaleBand, scaleLinear} from 'd3-scale'
import {select, selectAll} from 'd3-selection'
import d3Tip from '../d3/tip'
import {fetchPoll} from '@github-ui/fetch-utils'
import formatSymbol from '../d3/format-symbol'
import {line} from 'd3-shape'
import memoize from '@github/memoize'
import {timeWeeks} from 'd3-time'
import {utcFormat} from 'd3-time-format'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GraphNetworkData = any

const cachedData = memoize(fetchData)
async function fetchData(url: string): Promise<GraphNetworkData> {
  const response = await fetchPoll(url, {headers: {accept: 'application/json'}})
  return await response.json()
}

@controller
class CommitActivityGraphElement extends HTMLElement {
  @target graph: HTMLElement

  async connectedCallback() {
    const graph = this.graph
    const url = graph.getAttribute('data-url')
    if (!url) {
      return
    }

    // Remove possibly existing cached graph.
    /* eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback */
    for (const el of graph.querySelectorAll('svg.viz')) {
      el.remove()
    }

    graph.classList.add('is-graph-loading')
    graph.classList.remove('is-graph-load-error', 'is-graph-empty')

    try {
      const data = await cachedData(url)
      graph.classList.remove('is-graph-loading')
      if (data != null && data.unusable) {
        graph.classList.add('is-graph-without-usable-data')
      } else if (
        (data != null && data.length === 0) ||
        (data != null && data.summary != null && data.summary.total === 0) ||
        (data[0] != null && data[0].weeks != null && data[0].weeks.length === 0)
      ) {
        graph.classList.add('is-graph-empty')
      } else {
        render(graph, data)
      }
    } catch (error) {
      graph.classList.remove('is-graph-loading')
      graph.classList.add('is-graph-load-error')
      throw error
    }
  }
}

function render(container: HTMLElement, data: unknown[]) {
  const mcanvas = container.querySelector<HTMLElement>('#commit-activity-master')!
  const dcanvas = container.querySelector<HTMLElement>('#commit-activity-detail')!
  let h = 260
  const w = dcanvas.clientWidth
  let week = 0
  let transitionToWeek = null
  ;(function () {
    let i
    let j
    let len
    let lastActiveWeek = 0
    for (i = j = 0, len = data.length; j < len; i = ++j) {
      const d = data[i]
      if (d.total !== 0) {
        lastActiveWeek = i
      }
      data[i].index = i
    }
    week = lastActiveWeek
    const [padt, padl, padr, padb] = [20, 30, 30, 40]
    let days = data[week].days

    let max = d3max(data, function (d) {
      return d3max(d.days)
    })

    const x = scaleLinear()
      .domain([0, days.length - 1])
      .range([0, w - padl - padr])
    const y = scaleLinear().domain([0, max]).range([h, 0])
    const yAxis = axisLeft(y)
      .ticks(5)
      .tickSize(-w + padr + padl)
    const vis = select(dcanvas)
      .data([days])
      .append('svg')
      .attr('viewBox', `0 0 ${w} ${h + padt + padb}`)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr('class', 'viz')
      .attr('data-hpc', '')
      .append('g')
      .attr('transform', `translate(${padl},${padt})`)
    vis.append('g').attr('class', 'y axis').call(yAxis)
    const xlblgroup = vis.append('g').attr('class', 'axis')
    const daylabels = xlblgroup
      .selectAll('.day')
      .data(weekdays)
      .enter()
      .append('g')
      .attr('class', 'day')
      .attr('transform', function (d, index) {
        return `translate(${x(index)}, ${h})`
      })
    daylabels
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '2em')
      .text(function (d) {
        return d
      })
    const path = line()
      .x(function (d, index) {
        return x(index)
      })
      .y(y)
    vis.append('path').attr('class', 'path').attr('d', path)
    const dots = vis
      .selectAll('g.dot')
      .data(days)
      .enter()
      .append('g')
      .attr('class', 'dot')
      .attr('transform', function (d, index) {
        return `translate(${x(index)}, ${y(d)})`
      })
    dots.append('circle').attr('r', 4)
    dots
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('class', 'tip')
      .attr('dy', -10)
      .text(function (d) {
        return d
      })
    transitionToWeek = function (props) {
      if (props.index.index >= 52 || props.index.index < 0) {
        return
      }
      function getTranslation(transform) {
        // Create a dummy g for calculation purposes only. This will never
        // be appended to the DOM and will be discarded once this function
        // returns.
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')

        // Set the transform attribute to the provided string value.
        g.setAttributeNS(null, 'transform', transform)

        // consolidate the SVGTransformList containing all transformations
        // to a single SVGTransform of type SVG_TRANSFORM_MATRIX and get
        // its SVGMatrix.
        const matrix = g.transform.baseVal.consolidate().matrix

        // As per definition values e and f are the ones for the translation.
        return [matrix.e, matrix.f]
      }
      week = props.index.index
      days = data[props.index.index].days
      max = d3max(days)
      x.domain([0, days.length - 1])
      const nodes = selectAll('.bar.mini').attr('class', 'bar mini').nodes()
      const bgroup = select(nodes[week]).attr('class', 'bar mini active')
      const bgtransform = getTranslation(bgroup.attr('transform'))
      focus
        .transition()
        .ease(easeBackOut)
        .duration(300)
        .attr('transform', `translate(${bgtransform[0] + 8}, 105)`)
      vis.selectAll('.path').data([days]).transition().duration(500).attr('d', path)
      vis
        .selectAll('g.dot')
        .data(days)
        .transition()
        .duration(300)
        .attr('transform', function (d, index) {
          return `translate(${x(index)}, ${y(d)})`
        })
      return vis
        .selectAll('text.tip')
        .data(days)
        .text(function (d) {
          return d
        })
    }
  })()
  const [padt, padl, padr, padb] = [10, 30, 20, 30]
  h = 100
  const totals = data.map(function (d) {
    return d.total
  })
  const max = d3max(totals)
  const format = utcFormat('%m/%d')
  const x = scaleBand()
    .domain(range(totals.length))
    .rangeRound([0, w - padl - padr])
    .padding(0.1)
  const y = scaleLinear().domain([0, max]).range([h, 0])
  const yAxis = axisLeft(y)
    .ticks(3)
    .tickSize(-w + padl + padr)
    .tickFormat(formatSymbol)
  const xAxis = axisBottom(x)
    .ticks(timeWeeks)
    .tickFormat(function (d, i) {
      const date = new Date(data[i].week * 1000)
      return format(date)
    })
  const tip = d3Tip()
    .attr('class', 'svg-tip')
    .offset([-10, 0])
    .html(function (d) {
      const curweek = new Date(d.week * 1000)
      const formatted = `${months[curweek.getUTCMonth()].slice(0, 3)} ${curweek.getUTCDate()}`
      // eslint-disable-next-line github/unescaped-html-literal
      return `<strong>${d.total}</strong> ${d.total === 1 ? 'commit' : 'commits'} the week of ${formatted}`
    })
  const vis = select(mcanvas)
    .append('svg')
    .attr('viewBox', `0 0 ${w + (padl + padr)} ${h + padt + padb}`)
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .attr('class', 'viz')
    .attr('data-hpc', '')
    .append('g')
    .attr('transform', `translate(${padl},${padt})`)
    .call(tip)
  vis.append('g').attr('class', 'y axis').call(yAxis)
  const bgroups = vis
    .selectAll('g.mini')
    .data(data)
    .enter()
    .append('g')
    .attr('class', function (d, i) {
      if (i === week) {
        return 'bar mini active'
      } else {
        return 'bar mini'
      }
    })
    .attr('transform', function (d, i) {
      return `translate(${x(i)}, 0)`
    })
    .on('click', function (d, i) {
      // eslint-disable-next-line @typescript-eslint/no-invalid-this
      return transitionToWeek && transitionToWeek({node: this, index: i, data: d})
    })
  bgroups
    .append('rect')
    .attr('width', x.bandwidth())
    .attr('height', function (d) {
      return h - y(d.total)
    })
    .attr('y', function (d) {
      return y(d.total)
    })
    .on('mouseover', tip.show)
    .on('mouseout', tip.hide)
  vis
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', `translate(0,${h})`)
    .call(xAxis)
    .selectAll('.tick')
    .style('display', function (d, i) {
      if (i % 3 !== 0) {
        return 'none'
      } else {
        return 'block'
      }
    })
  const focus = vis
    .append('circle')
    .attr('class', 'focus')
    .attr('r', 8)
    .attr('transform', `translate(${x(week) + x.bandwidth() / 2}, ${-h})`)
  focus
    .transition()
    .ease(easeElasticOut)
    .duration(1000)
    .attr('r', 2)
    .attr('transform', `translate(${x(week) + x.bandwidth() / 2}, ${h + 5})`)
}
